import type {
  AuspiciousActivity,
  AuthInitRequest,
  AuthInitResponse,
  Gender,
  LunarDay,
  PublicUser,
  SkuId,
} from '@yadviga/shared'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

export interface ApiErrorBody {
  error: { code: string; message: string; details?: Record<string, unknown> }
  requestId?: string
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody,
  ) {
    super(body.error.message)
    this.name = 'ApiError'
  }
  get code(): string { return this.body.error.code }
  get details(): Record<string, unknown> | undefined { return this.body.error.details }
}

let currentJwt: string | null = null
export function setApiJwt(jwt: string | null): void {
  currentJwt = jwt
}

let on401: (() => void) | null = null
let on402: ((err: ApiError) => void) | null = null

/** Wire global handlers — typically from App after providers mount. */
export function configureApi(handlers: { on401?: () => void; on402?: (err: ApiError) => void }): void {
  on401 = handlers.on401 ?? null
  on402 = handlers.on402 ?? null
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body !== undefined && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (currentJwt) headers.set('Authorization', `Bearer ${currentJwt}`)
  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers })
  if (!res.ok) {
    let body: ApiErrorBody
    try {
      body = (await res.json()) as ApiErrorBody
    } catch {
      body = { error: { code: 'UNKNOWN', message: res.statusText } }
    }
    const err = new ApiError(res.status, body)
    if (res.status === 401 && on401) on401()
    if (res.status === 402 && on402) on402(err)
    throw err
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// ---------- typed payloads ----------

export interface DailyCardPayload {
  cardId: string
  reversed: boolean
  interpretation: string
  date: string
  isNew: boolean
}

export type SpreadKind = '3card' | '5card_relationship' | 'celtic_cross'
export type SpreadCategory = 'love' | 'career' | 'decision' | 'growth' | 'general'

export interface SpreadCardRef { id: string; reversed: boolean; position: number }

export interface SpreadResultPayload {
  id: string
  kind: SpreadKind
  cards: SpreadCardRef[]
  interpretation: string
  tier: 'free' | 'paid'
  createdAt: string
}

export interface NatalChartData {
  sunLon: number
  sunSign: string
  moonLon: number
  moonSign: string
  ascLon: number | null
  ascSign: string | null
  birth: { date: string; time: string | null; lat: number | null; lon: number | null; timezone: string }
}

export interface NatalPayload {
  chart: NatalChartData
  bigThreeInterpretation: string
  fullInterpretation: string | null
  tier: 'teaser' | 'full'
  isNew: boolean
  /**
   * True when a background full-chart generation is in flight on the
   * server. The frontend keeps polling teaser while this is true and
   * re-renders once the chart flips to tier='full'. The user is also
   * notified via the bot when the result is ready.
   */
  generationPending: boolean
}

export interface DailyHoroscopePayload {
  sign: string
  date: string
  language: 'ru' | 'en'
  text: string
  cached: boolean
}

export interface WeeklyForecastPayload {
  id: string
  kind: 'weekly'
  periodStart: string
  periodEnd: string
  interpretation: string
  isNew: boolean
}

export interface PlaceSuggestion {
  id: string
  name: string
  displayName: string
  country: string | null
  lat: number
  lon: number
  timezone: string
}

export interface CompatScoreBreakdown {
  score: number
  sun: { user: string; partner: string; score: number; blurb: string }
  numerology: { user: number; partner: number; score: number; blurb: string }
}

export interface CompatPayload {
  id: string
  inviteToken: string
  /** Pre-built messenger deeplink — pass directly to ShareButton's `url`. */
  inviteLink: string
  tier: 'teaser' | 'full'
  unlockedVia: 'paid' | 'subscription' | 'referral' | 'free_period' | null
  score: CompatScoreBreakdown
  partnerName: string
  partnerBirthDate: string
  teaserText: string
  fullText: string | null
  createdAt: string
  updatedAt: string
}

export interface ChatTurn {
  role: 'user' | 'assistant'
  content: string
  createdAt: string
}

export interface ChatSessionSummary {
  id: string
  createdAt: string
  updatedAt: string
  turnCount: number
  preview: string | null
}

export interface ChatSessionFull {
  id: string
  createdAt: string
  updatedAt: string
  turnCount: number
  turnLimit: number
  messages: ChatTurn[]
}

export interface ChatPostResult {
  sessionId: string
  reply: ChatTurn
  turnCount: number
  turnLimit: number
  exhausted: boolean
}

export interface BugReportSummary {
  id: string
  text: string
  status: 'open' | 'resolved' | 'dismissed'
  createdAt: string
}

export interface EntitlementsSnapshot {
  isPremium: boolean
  plan: 'monthly' | 'yearly' | null
  provider: string | null
  currentPeriodEnd: string | null
  autoRenew: boolean
}

export interface PromoRedeemResult {
  kind: 'entitlement_period' | 'discount'
  /** entitlement_period only. */
  grantedDays?: number
  /** entitlement_period only. */
  currentPeriodEnd?: string
  /** discount only — % off. */
  discountPercent?: number
  /** discount only — pass back to createInvoice. */
  code?: string
}

export type MoonPhaseId =
  | 'new' | 'waxing_crescent' | 'first_quarter' | 'waxing_gibbous'
  | 'full' | 'waning_gibbous' | 'last_quarter' | 'waning_crescent'

export interface LunarTodayPayload {
  phase: MoonPhaseId
  /** 0..1 — 0 at new moon, 1 at full moon. */
  illumination: number
  nextNew: string
  nextFull: string
  energyHint: { ru: string; en: string }
}

export interface TransitAspectPayload {
  transitPlanet: string
  natalPlanet: string
  aspect: string
  orb: number
  exact: boolean
}

export interface PersonalDailyPayload {
  date: string
  interpretation: string
  transits: TransitAspectPayload[]
  cached: boolean
}

export interface CurrentTransitsPayload {
  computedAt: string
  natalTier: 'teaser' | 'full'
  summary: string
  transits: Array<TransitAspectPayload & { text: string }>
}

export interface RetrogradePeriodPayload {
  planet: 'mercury' | 'venus' | 'mars' | 'jupiter' | 'saturn'
  startDate: string
  endDate: string
  startSign: import('@yadviga/shared').ZodiacSignId
  endSign: import('@yadviga/shared').ZodiacSignId
  durationDays: number
  description: { ru: string; en: string }
}

export interface InvoicePayload {
  payUrl: string
  providerPaymentId: string
  provider: string
  sku: SkuId
}

// ---------- API surface ----------

export interface UpdateProfilePayload {
  name?: string
  gender?: Gender
  language?: 'ru' | 'en'
  birthDate?: string
  birthTime?: string | null
  birthPlaceLat?: number | null
  birthPlaceLon?: number | null
  birthPlaceName?: string | null
  timezone?: string
}

function updateProfile(payload: UpdateProfilePayload): Promise<PublicUser> {
  return request('/users/me', { method: 'PATCH', body: JSON.stringify(payload) })
}

export const api = {
  // auth
  authInit(payload: AuthInitRequest): Promise<AuthInitResponse> {
    return request('/auth/init', { method: 'POST', body: JSON.stringify(payload) })
  },

  // users
  me(): Promise<PublicUser> {
    return request('/users/me')
  },
  updateProfile,
  /** @deprecated use updateProfile */
  updateBirth: updateProfile,

  // tarot
  dailyCard(): Promise<DailyCardPayload> {
    return request('/tarot/daily')
  },
  createSpread(payload: { kind: SpreadKind; question?: string; category?: SpreadCategory }): Promise<SpreadResultPayload> {
    return request('/tarot/spread', { method: 'POST', body: JSON.stringify(payload) })
  },

  // astro
  natalTeaser(): Promise<NatalPayload> {
    return request('/astro/natal/teaser', { method: 'POST' })
  },
  natalFull(): Promise<NatalPayload> {
    return request('/astro/natal/full', { method: 'POST' })
  },
  /**
   * Send the natal-chart PDF to the user's bot chat as a Document. The
   * preferred delivery path because TG/MAX WebView can't reliably trigger
   * an <a download> on mobile.
   */
  sendNatalPdfToChat(): Promise<{ delivered: boolean; platform: 'telegram' | 'max' }> {
    return request('/astro/natal/full.pdf/send', { method: 'POST' })
  },
  horoscopeToday(): Promise<DailyHoroscopePayload> {
    return request('/astro/horoscope/today')
  },

  /** Free retrograde calendar — 12 months ahead by default. */
  retrogrades(months = 12): Promise<RetrogradePeriodPayload[]> {
    return request(`/astro/retrogrades?months=${months}`)
  },

  /**
   * Current transits to the user's natal chart. Returns null when the user
   * hasn't filled in birth data yet.
   */
  currentTransits(): Promise<CurrentTransitsPayload | null> {
    return request('/astro/transits')
  },

  // daily — lunar widget (free) + personal daily reading (premium, cached)
  lunarToday(): Promise<LunarTodayPayload> {
    return request('/daily/lunar')
  },
  /** Lunar calendar — start at `from` (default today), `days` long
   *  (default 30, max 42). Free, requires JWT. */
  lunarMonth(opts?: { from?: string; tz?: string; days?: number }): Promise<LunarDay[]> {
    const params = new URLSearchParams()
    if (opts?.from) params.set('from', opts.from)
    if (opts?.tz) params.set('tz', opts.tz)
    if (opts?.days != null) params.set('days', String(opts.days))
    const qs = params.toString()
    return request(`/daily/lunar/month${qs ? `?${qs}` : ''}`)
  },
  /** Filter the next 30 days down to those auspicious for the activity. */
  lunarAuspicious(opts: { activity: AuspiciousActivity; days?: number; tz?: string }): Promise<LunarDay[]> {
    const params = new URLSearchParams()
    params.set('activity', opts.activity)
    if (opts.days != null) params.set('days', String(opts.days))
    if (opts.tz) params.set('tz', opts.tz)
    return request(`/daily/lunar/auspicious?${params.toString()}`)
  },
  personalDailyToday(): Promise<PersonalDailyPayload> {
    return request('/daily/personal')
  },

  // chat — multi-turn "Ask Yadviga" (premium-only)
  startChatSession(): Promise<ChatSessionFull> {
    return request('/chat/sessions', { method: 'POST' })
  },
  listChatSessions(limit = 30): Promise<ChatSessionSummary[]> {
    return request(`/chat/sessions?limit=${limit}`)
  },
  getChatSession(id: string): Promise<ChatSessionFull> {
    return request(`/chat/sessions/${id}`)
  },
  postChatMessage(id: string, content: string): Promise<ChatPostResult> {
    return request(`/chat/sessions/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    })
  },

  // feedback — user-submitted bug reports / "оставить баг"
  submitBugReport(payload: {
    text: string
    context?: Record<string, unknown>
  }): Promise<BugReportSummary> {
    return request('/feedback/bug', { method: 'POST', body: JSON.stringify(payload) })
  },
  listMyBugReports(): Promise<BugReportSummary[]> {
    return request('/feedback/bug/mine')
  },

  // entitlements — for profile badge + premium-aware UIs
  entitlementsMe(): Promise<EntitlementsSnapshot> {
    return request('/entitlements/me')
  },

  // promo codes — redeem (entitlement) or validate-for-discount
  redeemPromo(code: string): Promise<PromoRedeemResult> {
    return request('/promo/redeem', { method: 'POST', body: JSON.stringify({ code }) })
  },

  weeklyForecast(): Promise<WeeklyForecastPayload> {
    return request('/astro/forecast/weekly', { method: 'POST' })
  },
  /** GET — returns the cached current-week forecast if available, else null. */
  weeklyForecastCurrent(): Promise<WeeklyForecastPayload | null> {
    return request('/astro/forecast/weekly')
  },

  // compat
  createCompatTeaser(payload: { partnerName: string; partnerBirthDate: string; partnerBirthTime?: string | null; partnerBirthPlaceLat?: number | null; partnerBirthPlaceLon?: number | null }): Promise<CompatPayload> {
    return request('/compat/teaser', { method: 'POST', body: JSON.stringify(payload) })
  },
  unlockCompatFull(id: string): Promise<CompatPayload> {
    return request(`/compat/${id}/unlock-full`, { method: 'POST' })
  },
  redeemCompatInvite(token: string): Promise<CompatPayload> {
    return request('/compat/redeem-invite', { method: 'POST', body: JSON.stringify({ token }) })
  },
  listCompats(): Promise<CompatPayload[]> {
    return request('/compat')
  },
  getCompat(id: string): Promise<CompatPayload> {
    return request(`/compat/${id}`)
  },
  deleteCompat(id: string): Promise<void> {
    return request(`/compat/${id}`, { method: 'DELETE' })
  },

  // trial
  trialStatus(): Promise<{ eligible: boolean; activeUntil: string | null }> {
    return request('/me/trial/status')
  },
  trialStart(): Promise<{ eligible: boolean; activeUntil: string | null }> {
    return request('/me/trial/start', { method: 'POST' })
  },

  // dreams
  dreamDecode(text: string): Promise<{
    id: string
    text: string
    interpretation: string
    moonPhase: string | null
    createdAt: string
  }> {
    return request('/dreams/decode', { method: 'POST', body: JSON.stringify({ text }) })
  },
  dreamsHistory(): Promise<Array<{
    id: string
    preview: string
    moonPhase: string | null
    createdAt: string
  }>> {
    return request('/dreams/history')
  },
  dreamGet(id: string): Promise<{
    id: string
    text: string
    interpretation: string
    moonPhase: string | null
    createdAt: string
  }> {
    return request(`/dreams/${id}`)
  },
  dreamDelete(id: string): Promise<{ ok: true }> {
    return request(`/dreams/${id}`, { method: 'DELETE' })
  },

  // pricing catalog
  getCatalog(): Promise<Array<{
    sku: string
    kind: 'subscription' | 'one_time'
    period: 'monthly' | 'yearly' | null
    priceStars: number
    priceRub: number
    validUntil: string | null
    isOverride: boolean
    titleRu: string
    titleEn: string
    descriptionRu: string
    descriptionEn: string
    disabled: boolean
  }>> {
    return request('/payments/catalog')
  },

  // tarot history + share
  tarotHistory(limit = 50): Promise<Array<{
    id: string
    kind: '3card' | '5card_relationship' | 'celtic_cross'
    category: string | null
    question: string | null
    preview: string
    cardCount: number
    shareToken: string | null
    createdAt: string
  }>> {
    return request(`/tarot/history?limit=${limit}`)
  },
  tarotGet(id: string): Promise<{
    id: string
    kind: '3card' | '5card_relationship' | 'celtic_cross'
    cards: Array<{ id: string; reversed: boolean; position: number }>
    interpretation: string
    tier: 'free' | 'paid'
    createdAt: string
    shareToken?: string | null
  }> {
    return request(`/tarot/${id}`)
  },
  tarotShare(id: string): Promise<{ shareToken: string; shareUrl: string }> {
    return request(`/tarot/${id}/share`, { method: 'POST' })
  },
  tarotDelete(id: string): Promise<{ ok: true }> {
    return request(`/tarot/${id}`, { method: 'DELETE' })
  },
  tarotShared(token: string): Promise<{
    id: string
    kind: '3card' | '5card_relationship' | 'celtic_cross'
    cards: Array<{ id: string; reversed: boolean; position: number }>
    interpretation: string
    question: string | null
    category: string | null
    createdAt: string
  }> {
    // Endpoint is public; we still send Authorization if available — backend
    // just ignores it (the route has no JwtGuard).
    return request(`/tarot/shared/${token}`)
  },

  // referral
  getReferral(): Promise<{
    code: string
    link: string
    invitedCount: number
    daysEarned: number
    bonusDays: number
  }> {
    return request('/me/referral')
  },

  // platform linking (TG ↔ MAX)
  getLinkStatus(): Promise<{ platforms: Array<'telegram' | 'max'> }> {
    return request('/me/link')
  },
  startLink(): Promise<{
    code: string
    deeplinkTelegram: string
    deeplinkMax: string
    expiresAt: string
  }> {
    return request('/me/link/start', { method: 'POST' })
  },
  redeemLink(code: string): Promise<{
    userId: string
    platforms: Array<'telegram' | 'max'>
    redeemerWasDissolved: boolean
    jwt?: string
  }> {
    return request('/me/link/redeem', { method: 'POST', body: JSON.stringify({ code }) })
  },

  // payments
  createInvoice(payload: { sku: SkuId; promoCode?: string }): Promise<InvoicePayload> {
    return request('/payments/invoice', { method: 'POST', body: JSON.stringify(payload) })
  },

  // places
  searchPlaces(q: string, lang: 'ru' | 'en' = 'ru'): Promise<PlaceSuggestion[]> {
    const params = new URLSearchParams({ q, lang })
    return request(`/places/search?${params.toString()}`)
  },
}
